import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { InspectedSiteSlide } from '@euler/app/tv/components/inspection/InspectedSiteSlide';
import { useSiteInspectionCards } from '@euler/app/tv/functions/useSiteInspectionCards';
import { BlurView } from '@euler/components/BlurView';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { DefectiveLevel } from '@euler/model';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { noop } from 'rxjs';

type SiteInspectionDetailTarget = (
  | {
      type: 'inspected-site';
    }
  | {
      type: 'custom-issue';
    }
) & {
  key: string;
  id: number;
};

type SiteInspectionDetailProps = {
  filter:
    | {
        type: 'system-module';
        target: SiteInspectionDetailTarget;
        currentSystemModId: number;
      }
    | {
        type: 'defective-level';
        defectiveLevel: DefectiveLevel;
      }
    | {
        type: 'maintenance-advice';
        maintenanceAdvice: string;
      };
};

export const SiteInspectionDetailScreen = wrapNavigatorScreen(
  ({ filter }: SiteInspectionDetailProps) => {
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();
    const { detail, inventoryQuery } = useTaskContext();
    const cards = useSiteInspectionCards(detail, inventoryQuery, filter);

    let initialIndex =
      filter.type === 'system-module'
        ? cards.findIndex(x => x.key === filter.target.key)
        : 0;
    if (initialIndex === -1) {
      initialIndex = 0;
    }

    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const insets = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();
    let slideWidth = width * 0.7;
    let slideHeight = slideWidth * (700 / 1108);
    const maxHeight = height - insets.top - insets.bottom - rpx(80) * 2;
    if (slideHeight > maxHeight) {
      slideHeight = maxHeight;
      slideWidth = (1108 / 700) * slideHeight;
    }

    const scrollOffset = useSharedValue(0);
    const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
    const onScroll = useAnimatedScrollHandler({
      onScroll: e => {
        scrollOffset.value = e.contentOffset.x;
      },
      onMomentumEnd(e) {
        runOnJS(setCurrentIndex)(Math.floor(e.contentOffset.x / slideWidth));
      },
    });

    useEffect(() => {
      if (initialIndex > 0) {
        scrollViewRef.current?.scrollTo({
          x: slideWidth * initialIndex,
          y: 0,
          animated: true,
        });
      }
    }, [initialIndex, scrollViewRef, slideWidth]);

    return (
      <View
        css={`
          flex: 1;
          justify-content: center;
        `}
      >
        <BlurView
          blurType="dark"
          blurAmount={5}
          style={StyleSheet.absoluteFill}
          reducedTransparencyFallbackColor="white"
        />

        <TouchableWithoutFeedback
          style={StyleSheet.absoluteFill}
          onPress={() => navigation.goBack()}
        >
          <View style={StyleSheet.absoluteFill}>
            {cards.length === 0 && (
              <EmptyPlaceholder
                iconSize={rpx(220)}
                text="暂无相关部位检测信息"
                css={`
                  flex: 1;
                `}
              />
            )}
            <TouchableOpacity
              css={`
                position: absolute;
                top: 24px;
                right: 24px;
              `}
              onPress={() => navigation.goBack()}
            >
              <Icon name="close" size={32} color="#fff" />
            </TouchableOpacity>
          </View>
        </TouchableWithoutFeedback>

        {cards.length > 0 && (
          <Animated.ScrollView
            ref={scrollViewRef}
            horizontal
            snapToInterval={slideWidth}
            decelerationRate="normal"
            showsHorizontalScrollIndicator={Platform.OS === 'web'}
            scrollEventThrottle={1}
            onScroll={onScroll}
            onMomentumScrollEnd={noop}
            contentContainerStyle={{
              justifyContent: 'center',
              alignItems: 'center',
              height: slideHeight + (Platform.OS === 'web' ? 30 : 0),
              paddingHorizontal: (width - slideWidth) / 2,
              paddingVertical: Platform.OS === 'web' ? 15 : 0,
            }}
            css={`
              flex-grow: 0;
            `}
          >
            <TouchableWithoutFeedback
              style={StyleSheet.absoluteFill}
              onPress={() => navigation.goBack()}
            >
              <View style={StyleSheet.absoluteFill} />
            </TouchableWithoutFeedback>
            {cards.map((card, index) => {
              return (
                <InspectedSiteSlide
                  card={card}
                  key={card.key}
                  width={slideWidth}
                  height={slideHeight}
                  index={index}
                  scrollOffset={scrollOffset}
                  visible={
                    Math.abs(index - currentIndex) <= 2 || Platform.OS === 'web'
                  }
                />
              );
            })}
          </Animated.ScrollView>
        )}
      </View>
    );
  },
  {
    presentation: 'transparentModal',
    animationEnabled: false,
    headerShown: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
  },
);
